import { Link, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import NotificationDropdown from './NotificationDropdown';
import DropdownUser from './DropdownUser';
import QuickActionsDropdown from '../Dropdowns/QuickActionsDropdown';
import Logo from '../../images/logo/logo.png';
import DarkModeSwitcher from './DarkModeSwitcher';
import { FaBars, FaRedo, FaTimes } from 'react-icons/fa';
import CalculatorWidget from '../Widgets/CalculatorWidget';
import { useStatusStore } from '@/stores/statusStore';
import { useSearch } from '../../contexts/SearchContext';
import { getSearchSuggestions, buildQueryDescription } from '@/utils/searchQueryParser';
import api from '@/config/axios';
import { CONTACTS, PLOTS, CROPS, CONTRACTS, INVOICES, PROPOSALS, LEADS } from '@/config/apiPaths';

interface SearchResult {
  id: number | string;
  title: string;
  type: 'plot' | 'crop' | 'contact' | 'status' | 'contract' | 'invoice' | 'proposal' | 'lead';
  status_id?: number;
}

interface HeaderProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  onRefresh?: () => void;
}

// Common styles
const toggleButtonClass = 'h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-boxdark-2';
const iconClass = 'h-5 w-5 text-gray-600 dark:text-gray-300';

const Header = ({
  toggleMobileSidebar,
  onRefresh,
}: HeaderProps) => {
  const { searchQuery, setSearchQuery, activeFilter, setActiveFilter, parsedQuery, isStructuredQuery } = useSearch();
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownType, setDropdownType] = useState<'none' | 'suggestions' | 'results'>('none');
  const navigate = useNavigate();
  const { statuses, fetchStatuses } = useStatusStore();
  const searchRequestIdRef = useRef(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch statuses on component mount
  useEffect(() => {
    if (statuses.length === 0) {
      fetchStatuses().catch(console.error);
    }
  }, [fetchStatuses, statuses.length]);


  // Auto-close dropdown after 15 seconds
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    
    if (dropdownType !== 'none') {
      timer = setTimeout(() => {
        setDropdownType('none');
      }, 15000); // 15 seconds
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [dropdownType]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (!query.trim()) {
      // Clear everything if query is empty
      setSearchResults([]);
      setSearchSuggestions([]);
      setDropdownType('none');
      setIsLoading(false);
      return;
    }
    
    // Show suggestions for partial queries or incomplete structured queries
    if (query.length >= 1 && (query.length < 3 || (query.includes('.') && !query.includes(':')) || (query.includes(':') && !query.split(':')[1]?.trim()))) {
      const suggestions = getSearchSuggestions(query);
      setSearchSuggestions(suggestions);
      setDropdownType(suggestions.length > 0 ? 'suggestions' : 'none');
      return;
    }
    
    // Perform full search for queries >= 3 characters
    if (query.length >= 3) {
      setIsLoading(true);
      setDropdownType('results');

      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      searchDebounceRef.current = setTimeout(() => {
        void runSearch(query);
      }, 300); // Debounce to avoid firing a request on every keystroke
    }
  };

  const runSearch = async (query: string) => {
    const requestId = ++searchRequestIdRef.current;
    let results: SearchResult[] = [];

    if (isStructuredQuery) {
      // Handle structured queries - only show results for complete queries with values
      parsedQuery.queries.forEach(structuredQuery => {
        if (structuredQuery.value && structuredQuery.value.trim()) {
          // Map model names to correct types for navigation
          let resultType = structuredQuery.model.toLowerCase();

          // Ensure we use the correct route names
          switch (resultType) {
            case 'activity':
              resultType = 'activity';
              break;
            case 'contract':
              resultType = 'contract';
              break;
            case 'contact':
              resultType = 'contact';
              break;
            case 'plot':
              resultType = 'plot';
              break;
            case 'crop':
              resultType = 'crop';
              break;
            case 'proposal':
              resultType = 'proposal';
              break;
            case 'lead':
              resultType = 'lead';
              break;
            case 'note':
              resultType = 'note';
              break;
          }

          results.push({
            id: `${structuredQuery.model}-${structuredQuery.field}`,
            title: `${structuredQuery.model}: ${structuredQuery.field} contains "${structuredQuery.value}"`,
            type: resultType as any
          });
        }
      });

      // Add free text results if present
      if (parsedQuery.freeText) {
        results.push({
          id: 'freetext',
          title: `Text search: "${parsedQuery.freeText}"`,
          type: 'contact'
        });
      }
    } else if (!query.includes('.') && !query.includes(':')) {
      // Regular free-text search - query the real entities in parallel, plus statuses.
      const statusResults: SearchResult[] = statuses.length > 0
        ? statuses
            .filter(status =>
              status.name?.toLowerCase().includes(query.toLowerCase()) ||
              status.description?.toLowerCase().includes(query.toLowerCase())
            )
            .map(status => ({
              id: status.id,
              title: `Status: ${status.name}`,
              type: 'status' as const,
              status_id: status.id
            }))
        : [];

      const params = { q: query };

      const [
        contactsRes,
        plotsRes,
        cropsRes,
        contractsRes,
        invoicesRes,
        proposalsRes,
        leadsRes,
      ] = await Promise.allSettled([
        api.get(CONTACTS.ALL, { params }),
        api.get(PLOTS.ALL, { params }),
        api.get(CROPS.ALL, { params }),
        api.get(CONTRACTS.ALL, { params }),
        api.get(INVOICES.ALL, { params }),
        api.get(PROPOSALS.ALL, { params }),
        api.get(LEADS.ALL, { params }),
      ]);

      const entityResults: SearchResult[] = [];

      if (contactsRes.status === 'fulfilled') {
        (contactsRes.value.data || []).forEach((c: any) => {
          entityResults.push({
            id: c.id,
            title: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email || 'Contact',
            type: 'contact',
          });
        });
      }

      if (plotsRes.status === 'fulfilled') {
        (plotsRes.value.data || []).forEach((p: any) => {
          entityResults.push({ id: p.id, title: p.title || 'Plot', type: 'plot' });
        });
      }

      if (cropsRes.status === 'fulfilled') {
        (cropsRes.value.data || []).forEach((c: any) => {
          entityResults.push({ id: c.id, title: c.name || 'Crop', type: 'crop' });
        });
      }

      if (contractsRes.status === 'fulfilled') {
        (contractsRes.value.data || []).forEach((c: any) => {
          entityResults.push({ id: c.id, title: c.title || 'Contract', type: 'contract' });
        });
      }

      if (invoicesRes.status === 'fulfilled') {
        (invoicesRes.value.data || []).forEach((i: any) => {
          entityResults.push({ id: i.id, title: i.invoice_number || 'Invoice', type: 'invoice' });
        });
      }

      if (proposalsRes.status === 'fulfilled') {
        (proposalsRes.value.data || []).forEach((p: any) => {
          entityResults.push({ id: p.id, title: p.proposal_title || 'Proposal', type: 'proposal' });
        });
      }

      if (leadsRes.status === 'fulfilled') {
        (leadsRes.value.data || []).forEach((l: any) => {
          entityResults.push({ id: l.id, title: l.name || l.subject || 'Lead', type: 'lead' });
        });
      }

      results = [...entityResults, ...statusResults];
    }

    // Ignore stale responses from a previous, superseded search
    if (requestId !== searchRequestIdRef.current) {
      return;
    }

    setSearchResults(results);
    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchResults[0]) {
      handleResultClick(searchResults[0]);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const handleResultClick = (result: SearchResult) => {
    // Close dropdown when clicking on a result
    setDropdownType('none');
    
    // Navigate based on result type - normalize the type for structured queries
    const normalizedType = result.type.toLowerCase();

    // Real entity results carry a numeric id and can deep-link to the record's
    // detail view via the same `?id=` pattern used by the entity pages
    // themselves (e.g. Plots.tsx, Contacts.tsx). Structured-query "echo"
    // results carry a synthetic string id and just land on the list page.
    const idParam = typeof result.id === 'number' ? `?id=${result.id}` : '';

    switch (normalizedType) {
      case 'plot':
      case 'plots':
        navigate(`/plots${idParam}`);
        break;
      case 'crop':
      case 'crops':
        navigate(`/crops${idParam}`);
        break;
      case 'contact':
      case 'contacts':
        navigate(`/contacts${idParam}`);
        break;
      case 'contract':
      case 'contracts':
        navigate(`/contracts${idParam}`);
        break;
      case 'invoice':
      case 'invoices':
        navigate(`/invoices${idParam}`);
        break;
      case 'activity':
      case 'activities':
        navigate('/schedule');
        break;
      case 'proposal':
      case 'proposals':
        navigate(`/proposals${idParam}`);
        break;
      case 'lead':
      case 'leads':
        navigate(`/leads${idParam}`);
        break;
      case 'note':
      case 'notes':
        navigate('/notes');
        break;
      case 'status':
        // For status searches, don't navigate - they're more like filters
        break;
      default:
        // For unknown types or freetext searches, stay on current page
        console.log('Unknown result type:', result.type);
        break;
    }
  };

  const closeDropdown = () => {
    setDropdownType('none');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion + ' ');
    inputRef.current?.focus();
    setDropdownType('none');
  };

  return (
    <header className="sticky top-0 z-999 flex w-full bg-white drop-shadow-1 dark:bg-boxdark dark:drop-shadow-none">
      <div className="flex flex-grow items-center justify-between px-4 py-3 md:px-6 2xl:px-8">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Logo - Only shows on mobile */}
          <Link className="flex h-10 items-center lg:hidden" to="/">
            <img src={Logo} alt="Logo" className="h-8 w-auto" />
          </Link>
          
          {/* Mobile Toggle Button */}
          <button
            aria-label="Toggle menu"
            onClick={(e) => {
              e.stopPropagation();
              toggleMobileSidebar();
            }}
            className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-boxdark-2"
            title="Toggle Menu"
          >
            <FaBars className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
          
         
        </div>

        {/* Search Bar - Hidden on mobile */}
        <div className="hidden flex-1 lg:block">
          <div className="relative w-full">
            <div className="relative w-full">
              {/* Search icon */}
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              
              {/* Search input */}
              <div className="rounded-lg bg-gray-50 dark:bg-meta-4 focus-within:ring-2 focus-within:ring-primary">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  className="block w-full bg-transparent border-0 py-2 pl-10 pr-4 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-0 sm:text-sm"
                  value={searchQuery}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
            </div>
            

          </div>
              {/* Unified Search Dropdown */}
              {dropdownType !== 'none' && (
                <div className="absolute left-0 mt-2 w-full max-w-md rounded-lg border border-stroke bg-white shadow-lg dark:border-strokedark dark:bg-boxdark-2 z-50">
                  {/* Header with close button */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-stroke dark:border-strokedark">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {dropdownType === 'suggestions' ? 'Search Suggestions' : 
                       isStructuredQuery ? 'Structured Search Results' : 'Search Results'}
                    </span>
                    <button
                      onClick={closeDropdown}
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Close search"
                    >
                      <FaTimes className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>

                  {/* Query description for structured queries */}
                  {dropdownType === 'results' && isStructuredQuery && (
                    <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-stroke dark:border-strokedark">
                      <span className="text-xs text-blue-700 dark:text-blue-300">
                        Query: {buildQueryDescription(parsedQuery)}
                      </span>
                    </div>
                  )}

                  {/* Loading state */}
                  {isLoading && (
                    <div className="px-4 py-6 text-center">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Searching...</span>
                    </div>
                  )}

                  {/* Content */}
                  {!isLoading && (
                    <ul className="max-h-80 overflow-y-auto">
                      {dropdownType === 'suggestions' && searchSuggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-boxdark-2 cursor-pointer border-b border-stroke dark:border-strokedark last:border-b-0"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          <div className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                            {suggestion}
                          </div>
                        </li>
                      ))}

                      {dropdownType === 'results' && searchResults.map((result) => {
                        const status = result.type === 'status' 
                          ? statuses.find(s => s.id === result.status_id)
                          : null;
                          
                        return (
                          <li
                            key={`${result.type}-${result.id}`}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-boxdark-2 cursor-pointer border-b border-stroke dark:border-strokedark last:border-b-0"
                            onClick={() => handleResultClick(result)}
                          >
                            <div className="flex items-center">
                              {status?.color && (
                                <span 
                                  className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                  style={ { backgroundColor: status.color }}
                                />
                              )}
                              <div className="truncate">
                                <div className="font-medium text-sm text-gray-900 dark:text-white">
                                  {result.title}
                                </div>
                                {result.type === 'status' && status?.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {status.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* No results message */}
                  {!isLoading && dropdownType === 'results' && searchResults.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No results found
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* Right side icons */}
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <DarkModeSwitcher />
            </div>
            <CalculatorWidget />
          </div>
          
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="hidden h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-boxdark-2 sm:flex"
              title="Refresh Data"
              aria-label="Refresh Data"
            >
              <FaRedo className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
          )}
          <QuickActionsDropdown />
          <NotificationDropdown />
          <DropdownUser />
        </div>
      </div>
    </header>
  );
};

export default Header;
